import { labelDesign } from '../../Label';
import PortalWrapper from 'components/PortalWrapper';
import { Listbox, Transition } from '@headlessui/react';
import tw from 'twin.macro';
import {
  BaseButtonProps,
  Intent,
} from '@teetsh/app/src/components/Buttons/BaseButtonProps';
import ButtonGroup from '@teetsh/app/src/components/Form/ButtonGroup';
import { DisplayButton } from '@teetsh/app/src/components/Form/Select2/Branded/DisplayButton';
import { ListButton } from '@teetsh/app/src/components/Form/Select2/Branded/ListButton';
import { ListOptions } from '@teetsh/app/src/components/Form/Select2/Branded/ListOptions';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
} from '@floating-ui/react-dom';
import { Option } from './types';
import React from 'react';

export type Value = string | number | undefined;

export interface Props<T> {
  options: Option<T>[];
  large?: boolean;
  id?: string;
  label?: string;
  value?: T;
  onChange: (v: T) => void;
  children?: React.ReactNode;
  withoutIcon?: boolean;
  withoutCheck?: boolean;
  intent?: Intent;
  loading?: boolean;
  noDisplayCurrent?: boolean;
  placement?: 'top' | 'bottom';
  fill?: boolean;
  portal?: boolean;
}

export default function BrandedSelect<T extends Value>(
  props: BaseButtonProps & Props<T>,
) {
  const {
    label,
    options,
    value,
    noDisplayCurrent,
    onChange,
    withoutIcon,
    withoutCheck,
    intent,
    placement = 'bottom',
    portal = false,
  } = props;
  const matchingOption = options.find(opt => opt.value === value);
  const { refs, floatingStyles } = useFloating({
    middleware: [
      offset(2),
      flip(),
      shift(),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
    ],
    placement,
    whileElementsMounted: autoUpdate,
  });

  return (
    <Listbox value={value} onChange={onChange}>
      {({ open }) => (
        <>
          {label && (
            <Listbox.Label css={[labelDesign, tw`mb-1`]}>{label}</Listbox.Label>
          )}
          <div ref={refs.setReference} tw="relative">
            <ButtonGroup fill={props.fill} intent={intent}>
              <DisplayButton<T>
                option={matchingOption}
                loading={props.loading}
                intent={intent}
                fill={props.fill}
                withIcon={!withoutIcon}
              />
              <ListButton placement={placement} intent={intent} />
            </ButtonGroup>
            <PortalWrapper portal={portal}>
              <div
                ref={refs.setFloating}
                style={{
                  ...floatingStyles,
                  zIndex: 50,
                }}
              >
                <Transition
                  show={!!open}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <ListOptions
                    options={options}
                    intent={intent}
                    withoutCheck={withoutCheck}
                    noDisplayCurrent={noDisplayCurrent}
                  />
                </Transition>
              </div>
            </PortalWrapper>
          </div>
        </>
      )}
    </Listbox>
  );
}
